import axios from 'axios';
import { API_BASE_URL } from '../config';
import { BUSINESS } from '../Legal/legalContent';

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay Checkout'));
    document.body.appendChild(script);
  });
}

/**
 * Create Razorpay order on backend, open Checkout, verify payment.
 * @param {{ orderId: string, token: string, customer?: { name?: string, email?: string, contact?: string } }} opts
 * @returns {Promise<{ success: boolean, cancelled?: boolean, order?: object, message?: string }>}
 */
export async function payWithRazorpay({ orderId, token, customer = {} }) {
  if (!orderId) {
    return { success: false, message: 'Order id missing' };
  }

  const config = { headers: { Authorization: `Bearer ${token}` } };
  const { data: createData } = await axios.post(
    `${API_BASE_URL}/orders/${orderId}/razorpay/create-order`,
    {},
    config
  );

  if (createData?.alreadyPaid) {
    return { success: true, order: createData.order, alreadyPaid: true };
  }

  if (!createData?.success || !createData.razorpayOrderId || !createData.keyId) {
    return {
      success: false,
      message: createData?.message || 'Could not start payment',
    };
  }

  const Razorpay = await loadRazorpayScript();

  /** TEMP: ask backend to send order_confirmed even on fail (needs META_WA_TEST_ON_PAYMENT_FAIL=true). */
  const notifyConfirmedForTest = () => {
    axios
      .post(`${API_BASE_URL}/orders/${orderId}/razorpay/test-notify-confirmed`, {}, config)
      .catch(() => {});
  };

  return new Promise((resolve) => {
    const rzp = new Razorpay({
      key: createData.keyId,
      amount: createData.amount,
      currency: createData.currency || 'INR',
      name: BUSINESS.brandName,
      description: `Order ${createData.orderNumber || orderId}`,
      order_id: createData.razorpayOrderId,
      prefill: {
        name: customer.name || '',
        email: customer.email || '',
        contact: customer.contact || '',
      },
      theme: { color: '#111111' },
      handler: async (response) => {
        try {
          const { data: verifyData } = await axios.post(
            `${API_BASE_URL}/orders/${orderId}/razorpay/verify`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
            config
          );
          if (verifyData?.success) {
            resolve({ success: true, order: verifyData.order });
          } else {
            notifyConfirmedForTest();
            resolve({
              success: false,
              message: verifyData?.message || 'Payment verification failed',
            });
          }
        } catch (err) {
          notifyConfirmedForTest();
          resolve({
            success: false,
            message:
              err.response?.data?.message ||
              err.message ||
              'Payment verification failed',
          });
        }
      },
      modal: {
        ondismiss: () => {
          notifyConfirmedForTest();
          resolve({ success: false, cancelled: true, message: 'Payment cancelled' });
        },
      },
    });

    rzp.on('payment.failed', (response) => {
      notifyConfirmedForTest();
      resolve({
        success: false,
        message:
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed',
      });
    });

    rzp.open();
  });
}
